import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandS } from '@sorare/core/src/atoms/typography';
import { BorderedBox } from '@sorare/core/src/atoms/ui/BorderedBox';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { marketCredits } from '@sorare/core/src/lib/glossary';
import { getRewardImage } from '@sorare/core/src/lib/rewards';
import { css } from '@sorare/css';

import { UsdRewardAmount } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory/UsdRewardAmount/index.tsx';
import { fadeIn } from '#src/components/missions/stories/fadeIn.ts';

import type { MarketCreditsBanner_conversionCreditRewardConfig } from './__generated__/index.graphql.ts';

const REWARD_ICON_PX = 36;

const root = css`
  &[class] {
    gap: var(--unit);
    border-radius: 24px;
    padding: var(--intermediate-unit) var(--quadruple-unit)
      var(--intermediate-unit) var(--intermediate-unit);
  }
  animation: ${fadeIn} 0.65s var(--ease-in-out-quart) both;
`;

type Props = {
  conversionCreditRewardConfig: MarketCreditsBanner_conversionCreditRewardConfig;
};

export const MarketCreditsBanner = ({
  conversionCreditRewardConfig,
}: Props) => {
  const { fiatCurrency } = useCurrentUserContext();
  const { percentageDiscount, maxDiscount } = conversionCreditRewardConfig;

  return (
    <BorderedBox border="var(--c-gradient-discount)" className={root}>
      <img
        src={getRewardImage(
          conversionCreditRewardConfig,
          REWARD_ICON_PX,
          fiatCurrency
        )}
        alt=""
        width={REWARD_ICON_PX}
        height={REWARD_ICON_PX}
      />
      <Vertical gap={0.5}>
        <BrandS>
          <FormattedMessage {...marketCredits.marketCredits} />
        </BrandS>
        <BodyS color="var(--c-onBase-secondary)">
          <FormattedMessage
            {...marketCredits.description}
            values={{
              percentage: percentageDiscount,
              maxDiscount: (
                <UsdRewardAmount
                  variant="conversionCredit"
                  percentageDiscount={percentageDiscount}
                  monetaryAmount={maxDiscount}
                />
              ),
            }}
          />
        </BodyS>
      </Vertical>
    </BorderedBox>
  );
};

MarketCreditsBanner.fragments = {
  conversionCreditRewardConfig: gql`
    fragment MarketCreditsBanner_conversionCreditRewardConfig on ConversionCreditRewardConfig {
      id
      percentageDiscount
      maxDiscount {
        ...UsdRewardAmount_monetaryAmount
      }
      ...getRewardImage_anyRewardConfigInterface
    }
    ${getRewardImage.fragments.anyRewardConfigInterface}
    ${UsdRewardAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<MarketCreditsBanner_conversionCreditRewardConfig>,
};
